/**
 * Block 14 «Раскрой» — PR #3-DB EP-1 (ADR-0065). Маппинг редактируемого
 * состояния ↔ серверные контракты `cutting-*` (`@pomoshnik/shared`).
 *
 * Две границы:
 *   1. **document ↔ ProjectData** — редактор стора (`CuttingDocument` =
 *      meta+settings+parts) ↔ рабочая нагрузка `data` (§5.1.9 + D3).
 *   2. **projectState ↔ upsert/project** — R2-симметрия (handover §6.5):
 *      `state → контракт-payload → state'` сохраняет ВСЕ поля. Server-side поля
 *      (`userId`/`createdAt`/`updatedAt`) в state не входят (P-010, §7.2).
 *
 * `checksum` — детерминированный хеш рабочей нагрузки (change-detection в
 * local-first; реальный MD5 — в инфра-фазе). Чистые функции, без сети.
 */

import type {
  CuttingDraft,
  CuttingProject,
  CuttingProjectStatus,
  CuttingProjectUpsert,
  EdgesConfig,
  Offcut,
  OptimizationResult,
  ProjectData,
} from '@pomoshnik/shared';
import { CuttingDocumentSchema } from '../model/schemas';
import { isCompletePart } from '../model/partsUtils';
import type { CuttingDocument, Part } from '../model/types';

/**
 * Срезать feature-local поля детали, которых НЕТ в замороженном контракте
 * `ProjectData.parts` (ADR-0065, P-011): `grooves` (пазы, #10b-1) и `processing`
 * (обработка, #10b-2). Оба персистятся только IDB-local (serialization.ts), как
 * `ProjectOffcut.name` (B-225). R2-честность: что не в контракте, то и не «грузится».
 *
 * Legacy `do` (контрактный флаг для EP-4-движка) ПРОИЗВОДИТСЯ из `processing`:
 * `do = processing !== ''` (PR #10b-2 — processing стал источником истины обработки,
 * `do` перестал быть независимым). `doType` не трогаем. Server-side персистенция
 * пазов/обработки — будущий CUT + ADR (B-232 grooves, B-233 processing).
 */
function toContractPart(p: Part): Omit<Part, 'grooves' | 'processing'> {
  const { grooves: _grooves, processing, ...rest } = p;
  return { ...rest, do: processing !== '' };
}

/** Дефолт кромок: ленты не заданы (§3.2 `{type1,type2,type3?,type4?}`, D-точка edges=string). */
export const DEFAULT_EDGES: EdgesConfig = { type1: '', type2: '' };

/** Стабильная сериализация (сорт. ключи) — детерминизм checksum независимо от порядка. */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
}

/** djb2 → hex. НЕ криптографический — только для change-detection (checksum). */
function hash32(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(16).padStart(8, '0');
}

/** Детерминированный checksum рабочей нагрузки (без самого поля checksum). */
export function computeChecksum(payload: Omit<ProjectData, 'checksum'>): string {
  return hash32(stableStringify(payload));
}

export interface DocumentToDataExtras {
  edges?: EdgesConfig;
  offcuts?: Offcut[];
  lastResult?: OptimizationResult;
  estimate?: unknown;
}

/** `CuttingDocument` → `ProjectData` (data-конверт §5.1.9). checksum считается. */
export function documentToProjectData(
  doc: CuttingDocument,
  extras: DocumentToDataExtras = {},
): ProjectData {
  const base: Omit<ProjectData, 'checksum'> = {
    settings: doc.settings,
    sheetConfig: doc.settings.sheetConfig, // §5.1.9: data.sheetConfig — зеркало
    // PR #6a: незаполненные строки-«леса» таблицы (0×0) — UI-only scaffolding, в
    // валидируемый контракт (PartSchema.positive) не идут (R2 честный — что
    // персистится, то и грузится). Локальный IDB-кэш держит их через serialization.ts.
    // PR #10b-1/#10b-2: grooves (пазы) + processing (обработка) — feature-local,
    // срезаются (toContractPart); legacy `do` выводится из processing.
    parts: doc.parts.filter(isCompletePart).map(toContractPart),
    // Кромки: персистятся только НАЗВАНИЯ (метки) в type1/type2 (контракт ADR-0065);
    // толщина кромки — UI-only (B-211), в контракт не идёт (R2 честный).
    edges: extras.edges ?? { type1: doc.edges.edge1.name, type2: doc.edges.edge2.name },
    offcuts: extras.offcuts ?? [],
    // PR #24 (ADR-0081): per-project выбор «участвующих» складских остатков —
    // durable через ProjectData (id записей склада, P-001 verbatim).
    participatingOffcutIds: doc.participatingOffcutIds,
    ...(extras.lastResult !== undefined ? { lastResult: extras.lastResult } : {}),
    ...(extras.estimate !== undefined ? { estimate: extras.estimate } : {}),
  };
  return { ...base, checksum: computeChecksum(base) };
}

/** `CuttingDraft` → `CuttingDocument` (загрузка черновика в редактор). */
export function draftToDocument(draft: CuttingDraft): CuttingDocument {
  return CuttingDocumentSchema.parse({
    meta: { name: draft.name },
    settings: draft.data.settings,
    parts: draft.data.parts,
    // имена кромок восстанавливаем из контракта; толщина — UI-only, к дефолту (B-211)
    edges: { edge1: { name: draft.data.edges.type1 }, edge2: { name: draft.data.edges.type2 } },
    // PR #24 (ADR-0081): восстановить выбор «участвующих» остатков (durable)
    participatingOffcutIds: draft.data.participatingOffcutIds,
  });
}

/**
 * Редактируемое состояние проекта → `CuttingDocument` (загрузка в редактор).
 * Единый путь регидрации (A.4): и серверный `CuttingProject` (через
 * `projectToDocument` → `projectToState`), и локальная IDB-запись
 * `CuttingProjectRecord` (local-first, ADR-0090) — обе суть `CuttingProjectState`.
 */
export function projectStateToDocument(s: CuttingProjectState): CuttingDocument {
  return CuttingDocumentSchema.parse({
    meta: {
      name: s.name,
      materialText: s.materialText, // restore (R2 честный — поле в контракте)
      ...(s.customerName !== undefined ? { customerName: s.customerName } : {}),
      ...(s.dueDate !== undefined ? { dueDate: s.dueDate } : {}),
      ...(s.notes !== undefined ? { notes: s.notes } : {}),
    },
    settings: s.data.settings,
    parts: s.data.parts,
    edges: { edge1: { name: s.data.edges.type1 }, edge2: { name: s.data.edges.type2 } },
    // PR #24 (ADR-0081): восстановить выбор «участвующих» остатков (durable)
    participatingOffcutIds: s.data.participatingOffcutIds,
  });
}

/** `CuttingProject` (сервер) → `CuttingDocument` (загрузка проекта в редактор). */
export function projectToDocument(project: CuttingProject): CuttingDocument {
  return projectStateToDocument(projectToState(project));
}

/**
 * Редактируемое состояние проекта = контракт `CuttingProject` БЕЗ server-managed
 * полей (`userId`/`createdAt`/`updatedAt`). Полнота — для честного R2.
 */
export interface CuttingProjectState {
  id: string;
  name: string;
  materialText: string;
  materialId: string | null;
  status: CuttingProjectStatus;
  customerName?: string;
  dueDate?: string;
  notes?: string;
  data: ProjectData;
}

/** state → upsert-payload (все редактируемые поля переносятся). */
export function projectStateToUpsert(s: CuttingProjectState): CuttingProjectUpsert {
  return {
    id: s.id,
    name: s.name,
    materialText: s.materialText,
    materialId: s.materialId, // P-001 verbatim
    status: s.status,
    ...(s.customerName !== undefined ? { customerName: s.customerName } : {}),
    ...(s.dueDate !== undefined ? { dueDate: s.dueDate } : {}),
    ...(s.notes !== undefined ? { notes: s.notes } : {}),
    data: s.data,
  };
}

/** server `CuttingProject` → state (срезает server-managed поля). */
export function projectToState(p: CuttingProject): CuttingProjectState {
  const { userId: _u, createdAt: _c, updatedAt: _up, ...state } = p;
  return state;
}

/** upsert-payload → state (обратная сторона R2-симметрии). */
export function upsertToState(u: CuttingProjectUpsert): CuttingProjectState {
  return {
    id: u.id,
    name: u.name,
    materialText: u.materialText,
    materialId: u.materialId,
    status: u.status,
    ...(u.customerName !== undefined ? { customerName: u.customerName } : {}),
    ...(u.dueDate !== undefined ? { dueDate: u.dueDate } : {}),
    ...(u.notes !== undefined ? { notes: u.notes } : {}),
    data: u.data,
  };
}
